/**
 * Browser language detection — best-effort mapping of `navigator.language`
 * (and `navigator.languages`) to a SUPPORTED_LANGUAGES value.
 *
 * Phase 0 supports: en, es, pt-BR, ko, vi, gn. Everything else falls back
 * to `en` as the prompt language — the user can still type in any language,
 * and the Worker's `detect_language` handler (E4B classifier) will re-resolve.
 */

import type { Language } from '../api/types';

export function detectBrowserLanguage(): Language {
  const candidates: string[] = [];
  if (typeof navigator !== 'undefined') {
    if (Array.isArray(navigator.languages)) {
      candidates.push(...navigator.languages);
    }
    if (typeof navigator.language === 'string') {
      candidates.push(navigator.language);
    }
  }

  for (const raw of candidates) {
    const resolved = resolveLanguage(raw);
    if (resolved) return resolved;
  }
  return 'en';
}

function resolveLanguage(tag: string): Language | null {
  const lower = tag.toLowerCase();
  if (lower === 'pt-br' || lower.startsWith('pt-br')) return 'pt-BR';
  // Treat other Portuguese tags (pt, pt-PT) as pt-BR too — it's the best
  // match we have, and the Worker's E4B classifier will correct from the
  // user's typed response if needed.
  if (lower.startsWith('pt')) return 'pt-BR';
  if (lower.startsWith('es')) return 'es';
  if (lower.startsWith('ko')) return 'ko';
  if (lower.startsWith('vi')) return 'vi';
  // Guaraní: gn, gn-PY
  if (lower.startsWith('gn')) return 'gn';
  if (lower.startsWith('en')) return 'en';
  return null;
}
